import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PrintConfig } from '@matix/types';
import { printConfigAPI } from '@matix/api-client';
import { useCurrencyStore } from '../../store/currency';

/**
 * The query key for the tenant print config.
 *
 * Exported so non-React contexts (e.g. the Charge-success path) can read the
 * cached value via `queryClient.getQueryData(PRINT_CONFIG_QUERY_KEY)` without
 * coupling to the hook itself.
 */
export const PRINT_CONFIG_QUERY_KEY = ['printConfig'] as const;

/**
 * Fetches the tenant's print config exactly once per authenticated session
 * and caches it for the rest of the session.
 *
 * Caching contract:
 *   - `staleTime: Infinity`         — never auto-refetch on focus or interval.
 *   - `gcTime: Infinity`            — never garbage-collect the cache.
 *   - `refetchOnWindowFocus: false` — cashier tabs are long-lived.
 *
 * Mount once near the top of the app shell (App.tsx) with `enabled` set to
 * the auth state, so the query is warm before the cashier ever hits Charge.
 *
 * If the tenant edits the receipt footer in the ERP mid-shift, the change
 * appears on next session login. That trade-off (one-time reload vs every-
 * charge fetch) is intentional — the Charge path has to be as fast as
 * possible and refetching for tenant footer text on every order is wasteful.
 */
export function usePrintConfig(options?: {
  /**
   * When `false`, skip fetching. Pass `enabled: isAuthenticated` from the app
   * shell so the query waits for login before firing.
   */
  enabled?: boolean;
}): UseQueryResult<PrintConfig, Error> {
  const query = useQuery<PrintConfig, Error>({
    queryKey: PRINT_CONFIG_QUERY_KEY,
    queryFn: () => printConfigAPI.get(),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  // Mirror the resolved base currency into a plain store so the cart math, the
  // money formatter and the order-request builder (none of them components)
  // can round at the tenant's precision — 3 decimals on KWD, 2 on EGP/SAR/AED.
  //
  // This runs DURING render, not in an effect, on purpose. This hook is
  // mounted at the top of the app shell, so writing the store here means the
  // children rendered later in the same pass already read the real precision.
  // Doing it in an effect left exactly one paint where a 0.136 KWD price was
  // formatted at 2 decimals as "0.14" — and nothing re-rendered afterwards to
  // correct it, because nobody subscribes to this store (all readers use
  // `getState()`). The write is idempotent and guarded on a real change.
  const currency = query.data?.currency;
  if (currency) {
    const store = useCurrencyStore.getState();
    if (
      !store.loaded ||
      store.currency.code !== currency.code ||
      store.currency.precision !== currency.precision
    ) {
      store.setCurrency(currency);
    }
  }

  return query;
}
